export type ContractType =
  | "auto-entrepreneur"
  | "interim"
  | "CDD"
  | "CDI"
  | "temps-partiel-CDD"
  | "temps-partiel-CDI"
  | "stage"
  | "contractuel"
  | "titulaire";

export type JobCategory = "BNSSA" | "MNS";

export interface Job {
  id: string;
  title: string;
  employer: string;
  location: string;
  department: string;
  category: JobCategory;
  contractType: ContractType;
  salary?: string;
  hourlyRate?: string;
  description: string;
  requirements: string[];
  benefits: string[];
  publishedAt: string;
  deadline?: string;
  /** Date de début souhaitée (YYYY-MM-DD) — tous contrats sauf AE & Intérim. */
  startDate?: string;
  /** Date de fin (YYYY-MM-DD) — uniquement CDD & Temps Partiel CDD. */
  endDate?: string;
  logo?: string;
  // Champs optionnels enrichis
  urgent?: boolean;
  logementPossible?: boolean;
  horairesFixes?: boolean;
  coursParticuliers?: boolean; // MNS uniquement
  possibiliteCDI?: boolean;    // CDD uniquement
  // Mission ponctuelle (AE & intérim) — un seul jour
  missionDate?: string;        // YYYY-MM-DD
  missionStartTime?: string;   // HH:MM
  missionEndTime?: string;     // HH:MM
  missionHours?: number;       // durée en heures
  missionPrice?: number;       // prix total mission (intérim uniquement, en €)
  // Activités (AE & intérim uniquement) — case à cocher
  activities?: string[];
  // Mission pourvue (AE & intérim uniquement) : un candidat a été retenu par le recruteur.
  // Déduit dynamiquement des candidatures (status 'retenu') — voir useFilledJobIds.
  isFilled?: boolean;
}

/**
 * Liste des activités cochables pour les annonces AE / Intérim.
 * (Maintenu côté `data/jobs.ts` pour rester la source de vérité partagée.)
 */
export const JOB_ACTIVITIES = [
  "Aquagym",
  "Enseignement scolaire",
  "Bébés nageurs",
  "Surveillance",
  "Autre",
] as const;

export type JobActivity = typeof JOB_ACTIVITIES[number];

export const CONTRACT_LABELS: Record<ContractType, string> = {
  "auto-entrepreneur": "Auto-Entrepreneur",
  "interim": "Intérim",
  "CDD": "CDD",
  "CDI": "CDI",
  "temps-partiel-CDD": "Temps Partiel CDD",
  "temps-partiel-CDI": "Temps Partiel CDI",
  "stage": "Stage",
  "contractuel": "Contractuel",
  "titulaire": "Titulaire FPT",
};

export const jobs: Job[] = [];

/**
 * Liste officielle des départements français (métropole + Corse + DROM)
 * au format "Nom (code)" — ex. "Rhône (69)", "Corse-du-Sud (2A)".
 *
 * IMPORTANT : ce format est volontairement aligné sur :
 *  - les annonces importées en masse (BulkJobImport),
 *  - l'extraction du code département dans `extractDepartmentCode` (jobUrl.ts),
 *    qui lit le code entre parenthèses pour construire les URLs SEO.
 * Utilisé par le menu déroulant "Département" du formulaire d'offre.
 */
export const FRENCH_DEPARTMENTS: string[] = [
  "Ain (01)", "Aisne (02)", "Allier (03)", "Alpes-de-Haute-Provence (04)",
  "Hautes-Alpes (05)", "Alpes-Maritimes (06)", "Ardèche (07)", "Ardennes (08)",
  "Ariège (09)", "Aube (10)", "Aude (11)", "Aveyron (12)",
  "Bouches-du-Rhône (13)", "Calvados (14)", "Cantal (15)", "Charente (16)",
  "Charente-Maritime (17)", "Cher (18)", "Corrèze (19)", "Corse-du-Sud (2A)",
  "Haute-Corse (2B)", "Côte-d'Or (21)", "Côtes-d'Armor (22)", "Creuse (23)",
  "Dordogne (24)", "Doubs (25)", "Drôme (26)", "Eure (27)",
  "Eure-et-Loir (28)", "Finistère (29)", "Gard (30)", "Haute-Garonne (31)",
  "Gers (32)", "Gironde (33)", "Hérault (34)", "Ille-et-Vilaine (35)",
  "Indre (36)", "Indre-et-Loire (37)", "Isère (38)", "Jura (39)",
  "Landes (40)", "Loir-et-Cher (41)", "Loire (42)", "Haute-Loire (43)",
  "Loire-Atlantique (44)", "Loiret (45)", "Lot (46)", "Lot-et-Garonne (47)",
  "Lozère (48)", "Maine-et-Loire (49)", "Manche (50)", "Marne (51)",
  "Haute-Marne (52)", "Mayenne (53)", "Meurthe-et-Moselle (54)", "Meuse (55)",
  "Morbihan (56)", "Moselle (57)", "Nièvre (58)", "Nord (59)",
  "Oise (60)", "Orne (61)", "Pas-de-Calais (62)", "Puy-de-Dôme (63)",
  "Pyrénées-Atlantiques (64)", "Hautes-Pyrénées (65)", "Pyrénées-Orientales (66)",
  "Bas-Rhin (67)", "Haut-Rhin (68)", "Rhône (69)", "Haute-Saône (70)",
  "Saône-et-Loire (71)", "Sarthe (72)", "Savoie (73)", "Haute-Savoie (74)",
  "Paris (75)", "Seine-Maritime (76)", "Seine-et-Marne (77)", "Yvelines (78)",
  "Deux-Sèvres (79)", "Somme (80)", "Tarn (81)", "Tarn-et-Garonne (82)",
  "Var (83)", "Vaucluse (84)", "Vendée (85)", "Vienne (86)",
  "Haute-Vienne (87)", "Vosges (88)", "Yonne (89)", "Territoire de Belfort (90)",
  "Essonne (91)", "Hauts-de-Seine (92)", "Seine-Saint-Denis (93)",
  "Val-de-Marne (94)", "Val-d'Oise (95)",
  "Guadeloupe (971)", "Martinique (972)", "Guyane (973)",
  "La Réunion (974)", "Mayotte (976)",
];

// Annonces statiques historiques supprimées — le site utilise désormais
// uniquement les annonces fictives créées dans la collection `recruiter_jobs`.
const _legacyJobs: Job[] = [
  {
    id: "1",
    title: "Agent de Sécurité Aquatique BNSSA",
    employer: "Piscine Municipale de Lyon",
    location: "Lyon",
    department: "Rhône (69)",
    category: "BNSSA",
    contractType: "CDD",
    salary: "1 850 € - 2 100 € brut/mois",
    description:
      "La Piscine Municipale de Lyon recrute un(e) Agent de Sécurité Aquatique titulaire du BNSSA pour la surveillance du bassin principal et des activités aquatiques durant la saison estivale.",
    requirements: [
      "Titulaire du BNSSA en cours de validité",
      "Certificat de révision PSE1 à jour",
      "Expérience en surveillance aquatique souhaitée",
      "Sens des responsabilités et réactivité",
    ],
    benefits: [
      "Mutuelle d'entreprise",
      "Tickets restaurant",
      "Transport remboursé à 50%",
    ],
    publishedAt: "2025-01-10",
    deadline: "2025-02-28",
  },
  {
    id: "2",
    title: "BNSSA — Agent de Surveillance Aquatique",
    employer: "Club Aquatique Méditerranée",
    location: "Marseille",
    department: "Bouches-du-Rhône (13)",
    category: "BNSSA",
    contractType: "auto-entrepreneur",
    hourlyRate: "15 € - 18 €/heure",
    description:
      "Club aquatique privé recherche un BNSSA auto-entrepreneur pour des missions ponctuelles de surveillance lors d'événements nautiques et de cours particuliers.",
    requirements: [
      "BNSSA valide",
      "Statut auto-entrepreneur ou disponibilité pour en créer un",
      "Ponctualité et professionnalisme",
    ],
    benefits: ["Flexibilité des horaires", "Missions variées"],
    publishedAt: "2025-01-12",
  },
  {
    id: "3",
    title: "Maître Nageur Sauveteur — CDI temps plein",
    employer: "Centre Aquatique Grand Ouest",
    location: "Rennes",
    department: "Ille-et-Vilaine (35)",
    category: "MNS",
    contractType: "CDI",
    salary: "2 200 € - 2 800 € brut/mois",
    description:
      "Le Centre Aquatique Grand Ouest recrute un(e) Maître Nageur Sauveteur diplomé(e) pour encadrer les cours de natation, assurer la surveillance et participer aux activités aquatiques proposées par l'établissement.",
    requirements: [
      "BEESAN ou BPJEPS AAN",
      "Carte professionnelle en cours de validité",
      "Expérience de 2 ans minimum",
      "Pédagogie et communication",
    ],
    benefits: [
      "CDI temps plein 35h",
      "13ème mois",
      "Mutuelle famille",
      "Plan d'épargne entreprise",
    ],
    publishedAt: "2025-01-08",
    deadline: "2025-03-15",
  },
  {
    id: "4",
    title: "MNS Titulaire — Fonction Publique Territoriale",
    employer: "Ville de Bordeaux",
    location: "Bordeaux",
    department: "Gironde (33)",
    category: "MNS",
    contractType: "titulaire",
    salary: "2 100 € - 2 500 € brut/mois",
    description:
      "La Ville de Bordeaux ouvre un concours pour un poste de Maître Nageur Sauveteur au sein de sa direction des sports aquatiques. Poste permanent au sein de la Fonction Publique Territoriale.",
    requirements: [
      "BEESAN ou BPJEPS AAN",
      "Réussite au concours de la FPT",
      "Carte professionnelle valide",
      "Nationalité française ou ressortissant UE",
    ],
    benefits: [
      "Poste permanent",
      "Retraite FPT",
      "Congés bonifiés",
      "Formation continue",
      "RIFSEEP",
    ],
    publishedAt: "2025-01-05",
    deadline: "2025-02-14",
  },
  {
    id: "5",
    title: "BNSSA — Intérim saison estivale",
    employer: "Aqua Interim Services",
    location: "Nice",
    department: "Alpes-Maritimes (06)",
    category: "BNSSA",
    contractType: "interim",
    salary: "12,50 € - 14 € brut/heure",
    description:
      "Agence d'intérim spécialisée dans les métiers aquatiques recherche des BNSSA disponibles pour des missions estivales sur la Côte d'Azur, en piscines privées, résidences et clubs balnéaires.",
    requirements: [
      "BNSSA valide avec révision PSE1",
      "Disponibilité mai-septembre",
      "Mobilité géographique",
    ],
    benefits: ["Prime de précarité", "Congés payés inclus", "IFM incluse"],
    publishedAt: "2025-01-14",
  },
  {
    id: "6",
    title: "Maître Nageur — Temps Partiel CDI",
    employer: "Fitness Aqua Club",
    location: "Paris",
    department: "Paris (75)",
    category: "MNS",
    contractType: "temps-partiel-CDI",
    salary: "1 400 € - 1 700 € brut/mois",
    description:
      "Club de fitness haut de gamme recherche un MNS pour animer des cours d'aquagym, aquabike et natation adultes, à raison de 20h par semaine.",
    requirements: [
      "BPJEPS AAN ou BEESAN",
      "Expérience en aquafitness",
      "Dynamisme et motivation",
    ],
    benefits: ["Accès gratuit au club", "Primes de performance", "Évolution possible"],
    publishedAt: "2025-01-13",
  },
  {
    id: "7",
    title: "Stagiaire BNSSA — Surveillance Piscine",
    employer: "Lycée Sportif Jules Ferry",
    location: "Toulouse",
    department: "Haute-Garonne (31)",
    category: "BNSSA",
    contractType: "stage",
    salary: "Gratification légale",
    description:
      "Lycée sportif accueille un stagiaire en formation BNSSA pour une immersion professionnelle de 4 semaines dans un environnement pédagogique actif.",
    requirements: [
      "En cours de formation BNSSA",
      "Convention de stage obligatoire",
      "Motivation et sérieux",
    ],
    benefits: ["Encadrement par un professionnel", "Expérience valorisante"],
    publishedAt: "2025-01-11",
  },
  {
    id: "8",
    title: "MNS Contractuel — Collectivité",
    employer: "Communauté de Communes Beaujolais",
    location: "Villefranche-sur-Saône",
    department: "Rhône (69)",
    category: "MNS",
    contractType: "contractuel",
    salary: "2 000 € - 2 300 € brut/mois",
    description:
      "Communauté de communes recrute un MNS contractuel pour renforcer l'équipe de son complexe aquatique intercommunal. Contrat d'un an renouvelable.",
    requirements: [
      "BPJEPS AAN ou BEESAN",
      "Carte professionnelle valide",
      "Expérience en collectivité appréciée",
    ],
    benefits: [
      "Mutuelle collectivité",
      "Régime indemnitaire",
      "Formation continue",
    ],
    publishedAt: "2025-01-09",
    deadline: "2025-02-20",
  },
  {
    id: "9",
    title: "MNS Auto-Entrepreneur — Cours particuliers & stages vacances",
    employer: "Aqua Coaching Côte Basque",
    location: "Biarritz",
    department: "Pyrénées-Atlantiques (64)",
    category: "MNS",
    contractType: "auto-entrepreneur",
    hourlyRate: "28 € - 40 €/heure",
    description:
      "Aqua Coaching Côte Basque, structure spécialisée dans l'apprentissage et le perfectionnement de la natation, recherche un(e) Maître Nageur Sauveteur en statut auto-entrepreneur pour assurer des cours particuliers (enfants & adultes), des stages durant les vacances scolaires et de l'aquaphobie en piscine partenaire à Biarritz et Anglet. Vous fixez librement votre tarif horaire dans la fourchette indicative ci-dessus, en fonction de votre expérience et des prestations proposées (cours individuel, duo, stage intensif).",
    requirements: [
      "BPJEPS AAN, BEESAN, Licence STAPS mention Natation ou MNS",
      "Carte professionnelle d'éducateur sportif en cours de validité",
      "PSE1 ou PSE2 à jour avec recyclage annuel",
      "Assurance responsabilité civile professionnelle",
      "Statut auto-entrepreneur actif (ou en cours de création)",
      "Expérience en pédagogie enfants appréciée",
      "Approche bienveillante, ponctualité et sens du service",
    ],
    benefits: [
      "Tarif horaire libre fixé par le candidat",
      "Planning 100% flexible (vous choisissez vos créneaux)",
      "Bassin chauffé mis à disposition gratuitement",
      "Clients fournis par la structure (zéro prospection)",
      "Paiement sous 7 jours après facturation",
      "Volume potentiel : 10 à 25h/semaine selon disponibilités",
    ],
    publishedAt: "2025-01-15",
    deadline: "2025-04-30",
  },
];
